import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { ArrowLeftIcon } from '@heroicons/react/24/outline';
import { apiService } from '../services/api';
import { JobOpening } from '../types/api';
import PublicHeader from '../components/PublicHeader';
import PublicFooter from '../components/PublicFooter';
import DocumentTitle from '../components/DocumentTitle';
import MetaDescription from '../components/MetaDescription';
import RichTextDisplay from '../components/RichTextDisplay';

interface FieldError {
  field: string;
  message: string;
}

const JobApplicationPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [job, setJob] = useState<JobOpening | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [jobError, setJobError] = useState<string | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<FieldError[]>([]);

  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    resume: null as File | null,
    motivation_letter: '',
  });

  useEffect(() => {
    const fetchJob = async () => {
      if (!id) return;
      
      try {
        setLoading(true);
        setJobError(null);
        const jobData = await apiService.getJobById(parseInt(id));
        setJob(jobData);
      } catch (err) {
        setJobError('Failed to load job details');
        console.error('Error fetching job:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchJob();
  }, [id]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    // Clear field-specific error when user starts typing
    setFieldErrors(prev => prev.filter(error => error.field !== name));
    if (formError) {
      setFormError(null);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setFormData(prev => ({ ...prev, resume: file }));
    // Clear field-specific error when user selects a file
    setFieldErrors(prev => prev.filter(error => error.field !== 'resume'));
    if (formError) {
      setFormError(null);
    }
  };

  const parseBackendError = (error: any): { formError: string | null; fieldErrors: FieldError[] } => {
    if (error.response?.data?.details && Array.isArray(error.response.data.details)) {
      const fieldErrors: FieldError[] = error.response.data.details.map((detail: any) => ({
        field: detail.path?.[0] || 'unknown',
        message: detail.message || 'Invalid input'
      }));
      
      return {
        formError: null,
        fieldErrors
      };
    }
    
    return {
      formError: error.response?.data?.error || 'Failed to submit application. Please check your input and try again.',
      fieldErrors: []
    };
  };

  const getFieldError = (fieldName: string): string | null => {
    const error = fieldErrors.find(err => err.field === fieldName);
    return error ? error.message : null;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!job) return;

    try {
      setSubmitting(true);
      setFormError(null);
      setFieldErrors([]);

      await apiService.submitApplication({
        job_id: job.id,
        first_name: formData.first_name,
        last_name: formData.last_name,
        email: formData.email,
        phone: formData.phone,
        motivation_letter: formData.motivation_letter,
        resume: formData.resume!,
      });
      
      // Redirect to success page with job information
      navigate(`/application/success?jobId=${job.id}&jobTitle=${encodeURIComponent(job.title)}`);
    } catch (err: any) {
      const { formError: newFormError, fieldErrors: newFieldErrors } = parseBackendError(err);
      setFormError(newFormError);
      setFieldErrors(newFieldErrors);
      console.error('Error submitting application:', err);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  if (jobError || !job) {
    return (
      <div className="min-h-screen bg-white">
        <PublicHeader />
        <div className="max-w-7xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-3xl font-bold text-gray-900">Error</h1>
            <p className="mt-4 text-gray-600">{jobError || 'Job not found'}</p>
            <Link
              to="/jobs"
              className="mt-6 inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Jobs
            </Link>
          </div>
        </div>
        <PublicFooter />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white">
      <DocumentTitle title={`Apply for ${job.title}`} suffix="Job Application" />
      <MetaDescription description={`Apply for the ${job.title} position. Submit your application and join our team.`} />
      <PublicHeader />

      {/* Main Content */}
      <main>
        <div className="max-w-4xl mx-auto py-16 px-4 sm:px-6 lg:px-8">
          <div className="mb-8">
            <Link
              to="/jobs"
              className="inline-flex items-center text-sm text-gray-500 hover:text-gray-700 mb-6"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-2" />
              Back to Jobs
            </Link>
          </div>

          {/* Job Details Section */}
          <div className="bg-white mb-12">
            <div className="mb-8">
              <h1 className="text-4xl font-bold text-gray-900 mb-4">{job.title}</h1>
              
              <div className="flex flex-wrap gap-4 text-sm text-gray-600 mb-6">
                {job.location && (
                  <div className="flex items-center">
                    <span className="mr-2">📍</span>
                    {job.location}
                  </div>
                )}
                {job.salary_range && (
                  <div className="flex items-center">
                    <span className="mr-2">💰</span>
                    {job.salary_range}
                  </div>
                )}
                <div className="flex items-center">
                  <span className="mr-2">📅</span>
                  Posted {new Date(job.created_at).toLocaleDateString()}
                </div>
              </div>
            </div>

            <div className="prose prose-lg max-w-none">
              <section className="mb-8">
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Job Description</h2>
                <RichTextDisplay content={job.description} />
              </section>

              {job.requirements && (
                <section className="mb-8">
                  <h2 className="text-2xl font-semibold text-gray-900 mb-4">Requirements</h2>
                  <RichTextDisplay content={job.requirements} />
                </section>
              )}
            </div>
          </div>

          {/* Application Form Section */}
          <div className="bg-gray-50 rounded-lg p-8">
            <div className="mb-8">
              <h2 className="text-3xl font-bold text-gray-900 mb-2">Apply for this Position</h2>
              <p className="text-gray-600">Please fill out the form below to submit your application.</p>
            </div>

            {formError && (
              <div className="mb-6 bg-red-50 border border-red-200 rounded-md p-4">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-red-800">Application Error</h3>
                    <div className="mt-2 text-sm text-red-700">{formError}</div>
                  </div>
                </div>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="grid grid-cols-1 gap-6 sm:grid-cols-2">
                <div>
                  <label htmlFor="first_name" className="block text-sm font-medium text-gray-700">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="first_name"
                    id="first_name"
                    required
                    placeholder="Enter your first name"
                    value={formData.first_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      getFieldError('first_name') ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {getFieldError('first_name') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('first_name')}</p>
                  )}
                </div>

                <div>
                  <label htmlFor="last_name" className="block text-sm font-medium text-gray-700">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="last_name"
                    id="last_name"
                    required
                    placeholder="Enter your last name"
                    value={formData.last_name}
                    onChange={handleInputChange}
                    className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                      getFieldError('last_name') ? 'border-red-300' : 'border-gray-300'
                    }`}
                  />
                  {getFieldError('last_name') && (
                    <p className="mt-1 text-sm text-red-600">{getFieldError('last_name')}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                  Email Address *
                </label>
                <input
                  type="email"
                  name="email"
                  id="email"
                  required
                  placeholder="Enter your email address"
                  value={formData.email}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    getFieldError('email') ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {getFieldError('email') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('email')}</p>
                )}
              </div>

              <div>
                <label htmlFor="phone" className="block text-sm font-medium text-gray-700">
                  Phone Number
                </label>
                <input
                  type="tel"
                  name="phone"
                  id="phone"
                  placeholder="Enter your phone number (optional)"
                  value={formData.phone}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    getFieldError('phone') ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                {getFieldError('phone') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('phone')}</p>
                )}
              </div>

              <div>
                <label htmlFor="resume" className="block text-sm font-medium text-gray-700">
                  Resume *
                </label>
                <input
                  type="file"
                  name="resume"
                  id="resume"
                  accept=".pdf,.doc,.docx"
                  required
                  onChange={handleFileChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    getFieldError('resume') ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <p className="mt-1 text-sm text-gray-500">
                  Accepted formats: PDF, DOC, DOCX (Max 5MB)
                </p>
                {getFieldError('resume') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('resume')}</p>
                )}
              </div>

              <div>
                <label htmlFor="motivation_letter" className="block text-sm font-medium text-gray-700">
                  Motivation Letter *
                </label>
                <textarea
                  name="motivation_letter"
                  id="motivation_letter"
                  rows={6}
                  required
                  placeholder="Tell us why you're interested in this position and why you would be a great fit for this role. Please provide at least 400 characters."
                  value={formData.motivation_letter}
                  onChange={handleInputChange}
                  className={`mt-1 block w-full px-3 py-2 border rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500 sm:text-sm ${
                    getFieldError('motivation_letter') ? 'border-red-300' : 'border-gray-300'
                  }`}
                />
                <div className="mt-1 flex justify-between text-sm">
                  <p className="text-gray-500">
                    Minimum 400 characters required
                  </p>
                  <p className={`${
                    formData.motivation_letter.length < 400 ? 'text-red-600' : 'text-green-600'
                  }`}>
                    {formData.motivation_letter.length}/400 characters
                  </p>
                </div>
                {getFieldError('motivation_letter') && (
                  <p className="mt-1 text-sm text-red-600">{getFieldError('motivation_letter')}</p>
                )}
              </div>

              <div className="flex justify-end space-x-4">
                <Link
                  to="/jobs"
                  className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </Link>
                <button
                  type="submit"
                  disabled={submitting}
                  className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {submitting ? 'Submitting...' : 'Submit Application'}
                </button>
              </div>
            </form>
          </div>
        </div>
      </main>

      <PublicFooter />
    </div>
  );
};

export default JobApplicationPage;
